import { Event, SDK, ServerOptions } from '@commercetools/frontend-sdk';
import { Product, FilterField, Category } from 'shared/types/product';
import { PaginatedResult, ProductPaginatedResult } from 'shared/types/result';
import {
  GetProductAction,
  ProductFiltersAction,
  ProductQueryAction,
  QueryProductCategoriesAction,
} from '../../types/actions/ProductActions';
import { ComposableCommerceEvents } from '../../types/events/ComposableCommerceEvents';
import { GetProductQuery, ProductQueryQuery, QueryProductCategoriesQuery } from '../../types/queries/ProductQueries';

export type ProductActions = {
  getProduct: GetProductAction;
  query: ProductQueryAction;
  queryCategories: QueryProductCategoriesAction;
  productFilters: ProductFiltersAction;
};

export const getProductActions = (sdk: SDK<ComposableCommerceEvents>): ProductActions => {
  return {
    getProduct: async (
      query: GetProductQuery,
      options: {
        parallel?: boolean;
        customHeaderValue?: string;
        serverOptions?: ServerOptions;
      } = {},
    ) => {
      const start = performance.now();

      try {
        const response = await sdk.callAction<Product>({
          actionName: 'product/getProduct',
          query,
          parallel: options.parallel,
          customHeaderValue: options.customHeaderValue,
          serverOptions: options.serverOptions,
        });

        const end = performance.now();
        const duration = (end - start).toFixed(2);

        if (response.isError) {
          console.error('[ERROR] product/getProduct failed', {
            durationMs: duration,
            error: response.error,
            requestId: response.tracing,
          });
        } else {
          console.log('[INFO] product/getProduct completed', {
            durationMs: duration,
            productId: response.data?.productId,
            requestId: response.tracing,
          });

          // Optional slow call warning (500ms threshold)
          if (Number(duration) > 500) {
            console.warn('[WARN] product/getProduct slow response', {
              durationMs: duration,
            });
          }

          // Log response headers if SDK exposes them
          if ((response as any)?.headers) {
            console.log('[INFO] product/getProduct response headers', response as any, response.tracing);
          }

          if (response.data) {
            sdk.trigger(
              new Event({
                eventName: 'productFetched',
                data: {
                  product: response.data,
                },
              }),
            );
          }
        }

        return response;
      } catch (error: any) {
        const end = performance.now();

        console.error('[ERROR] product/getProduct unexpected error', {
          durationMs: (end - start).toFixed(2),
          message: error?.message,
          stack: error?.stack,
        });

        throw error;
      }
    },
    query: async (
      query: ProductQueryQuery,
      options: {
        parallel?: boolean;
        customHeaderValue?: string;
        serverOptions?: ServerOptions;
      } = {},
    ) => {
      const start = performance.now();

      try {
        const response = await sdk.callAction<ProductPaginatedResult>({
          actionName: 'product/query',
          query,
          parallel: options.parallel,
          customHeaderValue: options.customHeaderValue,
          serverOptions: options.serverOptions,
        });

        const end = performance.now();
        const duration = (end - start).toFixed(2);

        if (response.isError) {
          console.error('product/query failed', {
            durationMs: duration,
            error: response.error,
            requestId: response.tracing,
          });
        } else {
          console.info('product/query completed', {
            durationMs: duration,
            resultCount: response.data?.items?.length,
            requestId: response.tracing,
          });

          // log headers if available
          if ((response as any)?.headers) {
            console.info('product/query response headers', (response as any).headers, response.tracing);
          }

          sdk.trigger(
            new Event({
              eventName: 'productsQueried',
              data: {
                query,
                result: response.data,
              },
            }),
          );
        }

        return response;
      } catch (error: any) {
        const end = performance.now();
        console.error('product/query unexpected error', {
          durationMs: (end - start).toFixed(2),
          message: error?.message,
          stack: error?.stack,
        });
        throw error;
      }
    },
    queryCategories: async (
      query: QueryProductCategoriesQuery,
      options: {
        parallel?: boolean;
        customHeaderValue?: string;
        serverOptions?: ServerOptions;
      } = {},
    ) => {
      const start = performance.now();

      try {
        const response = await sdk.callAction<PaginatedResult<Category>>({
          actionName: 'product/queryCategories',
          query,
          parallel: options.parallel,
          customHeaderValue: options.customHeaderValue,
          serverOptions: options.serverOptions,
        });

        const end = performance.now();
        const duration = (end - start).toFixed(2);

        if (response.isError) {
          console.error('[ERROR] product/queryCategories failed', {
            durationMs: duration,
            error: response.error,
            requestId: response.tracing,
          });
        } else {
          console.log('[INFO] product/queryCategories completed', {
            durationMs: duration,
            resultCount: response.data?.items?.length,
            requestId: response.tracing,
          });

          // Optional slow-call warning (500ms threshold)
          if (Number(duration) > 500) {
            console.warn('[WARN] product/queryCategories slow response', {
              durationMs: duration,
            });
          }

          // Log response headers if available
          if ((response as any)?.headers) {
            console.log('[INFO] product/queryCategories response headers', (response as any).headers, response.tracing);
          }

          sdk.trigger(
            new Event({
              eventName: 'productCategoriesQueried',
              data: {
                query,
                result: response.data,
              },
            }),
          );
        }

        return response;
      } catch (error: any) {
        const end = performance.now();

        console.error('[ERROR] product/queryCategories unexpected error', {
          durationMs: (end - start).toFixed(2),
          message: error?.message,
          stack: error?.stack,
        });

        throw error;
      }
    },
    productFilters: async (
      options: {
        parallel?: boolean;
        customHeaderValue?: string;
        serverOptions?: ServerOptions;
      } = {},
    ) => {
      const start = performance.now();

      try {
        const response = await sdk.callAction<FilterField[]>({
          actionName: 'product/productFilters',
          parallel: options.parallel,
          customHeaderValue: options.customHeaderValue,
          serverOptions: options.serverOptions,
        });

        const end = performance.now();
        const duration = (end - start).toFixed(2);

        if (response.isError) {
          console.error('[ERROR] product/productFilters failed', {
            durationMs: duration,
            error: response.error,
            requestId: response.tracing,
          });
        } else {
          console.log('[INFO] product/productFilters completed', {
            durationMs: duration,
            filterCount: response.data?.length,
            requestId: response.tracing,
          });

          // Optional slow-call warning (500ms threshold)
          if (Number(duration) > 500) {
            console.warn('[WARN] product/productFilters slow response', {
              durationMs: duration,
            });
          }

          // Log response headers if available
          if ((response as any)?.headers) {
            console.log('[INFO] product/productFilters response headers', (response as any).headers, response.tracing);
          }

          sdk.trigger(
            new Event({
              eventName: 'productFiltersFetched',
              data: {
                filterFields: response.data,
              },
            }),
          );
        }

        return response;
      } catch (error: any) {
        const end = performance.now();

        console.error('[ERROR] product/productFilters unexpected error', {
          durationMs: (end - start).toFixed(2),
          message: error?.message,
          stack: error?.stack,
        });

        throw error;
      }
    },
  };
};
